import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Envelope, ArrowLeft } from '@phosphor-icons/react';
import { isNativeApp } from '../lib/native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email,
        origin_url: window.location.origin,
      });
      setSubmitted(true);
      if (res.data?.dev_reset_url) {
        setDevResetUrl(res.data.dev_reset_url);
      }
      toast.success('Request received');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const copyDevLink = async () => {
    try {
      await navigator.clipboard.writeText(devResetUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className={`min-h-screen bg-background flex items-center justify-center px-4${isNativeApp() ? ' native-auth-screen' : ''}`}>
      <div className="noise-overlay" />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 mb-6">
            <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-2">FORGOT PASSWORD</h1>
          <p className="font-body text-slate-400">Enter your email and we'll send you a reset link</p>
        </div>

        <div className="bg-card border border-white/5 rounded-lg p-8" data-testid="forgot-password-card">
          {!submitted ? (
            <form onSubmit={handleSubmit} data-testid="forgot-password-form" className="space-y-6">
              <div>
                <label className="font-body text-sm font-medium mb-2 block" htmlFor="email">Email</label>
                <div className="relative">
                  <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="forgot-email-input"
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white placeholder:text-slate-400 pl-12 pr-4 font-body outline-none transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3.5"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="forgot-success-state">
              <div className="text-center">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gold/15 border border-gold/40 mb-4">
                  <Envelope size={26} className="text-gold" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-2">Check your email</h2>
                <p className="font-body text-slate-300">
                  If an account exists for <span className="text-gold">{email}</span>, a password reset link has been sent. The link expires in 60 minutes.
                </p>
              </div>

              {devResetUrl && (
                <div data-testid="dev-reset-link-box" className="mt-4 p-4 rounded-lg border border-amber-500/40 bg-amber-500/5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-body text-xs uppercase tracking-wide text-amber-300">Dev mode — no email provider configured</span>
                    <button
                      type="button"
                      onClick={copyDevLink}
                      data-testid="copy-dev-link-btn"
                      className="text-xs px-2 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 font-medium transition-colors"
                    >Copy</button>
                  </div>
                  <p className="font-body text-xs text-slate-300 mb-2">Open this link to reset your password:</p>
                  <a
                    data-testid="dev-reset-link"
                    href={devResetUrl}
                    className="block font-mono text-xs text-amber-200 break-all hover:text-amber-100 underline underline-offset-2"
                  >{devResetUrl}</a>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" data-testid="forgot-back-to-login" className="font-body text-sm text-slate-400 hover:text-gold transition-colors inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
